export type StageControls = {
  onPrev?: () => void
  onNext?: () => void
  onMark?: () => void
  onExit: () => void
  label?: string
}

const activeStages = new WeakMap<HTMLElement, () => void>()

export function openStage(root: HTMLElement, controls: StageControls): () => void {
  activeStages.get(root)?.()

  const previousBodyOverflow = document.body.style.overflow
  const chrome = document.querySelector<HTMLElement>('[data-mount-chrome]')
  const chromeWasHidden = chrome?.hidden ?? false

  root.hidden = false
  document.body.style.overflow = 'hidden'
  if (chrome) chrome.hidden = true

  const hud = root.querySelector<HTMLElement>('[data-stage-hud]')
  const prev = root.querySelector<HTMLButtonElement>('[data-stage="prev"]')
  const next = root.querySelector<HTMLButtonElement>('[data-stage="next"]')
  const mark = root.querySelector<HTMLButtonElement>('[data-stage="mark"]')
  const exit = root.querySelector<HTMLButtonElement>('[data-stage="exit"]')
  const fullscreen = root.querySelector<HTMLButtonElement>('[data-stage="fullscreen"]')
  const hide = root.querySelector<HTMLButtonElement>('[data-stage="hide"]')
  const label = root.querySelector<HTMLElement>('[data-stage-label]')
  const hint = root.querySelector<HTMLElement>('[data-stage-hint]')
  const hintNext = root.querySelector<HTMLElement>('[data-stage-hint-next]')
  const hintFullscreen = root.querySelector<HTMLElement>('[data-stage-hint-fullscreen]')

  if (label && controls.label) label.textContent = controls.label
  if (prev) prev.hidden = !controls.onPrev
  if (next) next.hidden = !controls.onNext
  if (mark) mark.hidden = !controls.onMark
  if (hintNext) hintNext.hidden = !controls.onNext

  let hintTimer = 0
  const showControls = () => {
    hud?.classList.remove('is-hidden')
    if (hint) hint.hidden = true
    window.clearTimeout(hintTimer)
  }
  const hideControls = () => {
    hud?.classList.add('is-hidden')
    if (!hint) return
    hint.hidden = false
    window.clearTimeout(hintTimer)
    hintTimer = window.setTimeout(() => {
      hint.hidden = true
    }, 2800)
  }

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      controls.onExit()
      return
    }
    if (event.key === 'ArrowLeft') controls.onPrev?.()
    if (event.key === 'ArrowRight') controls.onNext?.()
    if (event.key === 'm' || event.key === 'M') controls.onMark?.()
    if (event.key === 'h' || event.key === 'H') {
      if (hud?.classList.contains('is-hidden')) showControls()
      else hideControls()
      return
    }
  }

  const onPrevClick = () => controls.onPrev?.()
  const onNextClick = () => controls.onNext?.()
  const onMarkClick = () => controls.onMark?.()
  const onExitClick = () => controls.onExit()
  const requestFs = root.requestFullscreen?.bind(root)
  const syncFullscreenButton = () => {
    const isFullscreen = document.fullscreenElement === root
    root.classList.toggle('is-awaiting-fullscreen', Boolean(requestFs && !isFullscreen))
    if (fullscreen) fullscreen.hidden = !requestFs || isFullscreen
    if (hintFullscreen) hintFullscreen.hidden = !requestFs || isFullscreen
    if (hintNext) hintNext.hidden = !controls.onNext || !isFullscreen
  }
  const enterFullscreen = () => {
    if (!requestFs) return
    void requestFs()
      .then(() => {
        if (closed && document.fullscreenElement === root) return document.exitFullscreen()
      })
      .catch(() => {
        syncFullscreenButton()
        hideControls()
      })
  }
  const onFullscreenChange = () => {
    syncFullscreenButton()
    if (document.fullscreenElement === root) hideControls()
  }
  const onFullscreenClick = () => enterFullscreen()
  const onHideClick = () => hideControls()
  const onHiddenStageClick = (event: MouseEvent) => {
    if (!hud?.classList.contains('is-hidden')) return
    if (hud.contains(event.target as Node)) return
    if (requestFs && document.fullscreenElement !== root) {
      event.preventDefault()
      event.stopPropagation()
      if (hint) hint.hidden = true
      enterFullscreen()
      return
    }
    if (!controls.onNext) return
    event.preventDefault()
    event.stopPropagation()
    if (hint) hint.hidden = true
    controls.onNext()
  }

  prev?.addEventListener('click', onPrevClick)
  next?.addEventListener('click', onNextClick)
  mark?.addEventListener('click', onMarkClick)
  exit?.addEventListener('click', onExitClick)
  fullscreen?.addEventListener('click', onFullscreenClick)
  hide?.addEventListener('click', onHideClick)
  root.addEventListener('click', onHiddenStageClick, true)
  window.addEventListener('keydown', onKey)
  document.addEventListener('fullscreenchange', onFullscreenChange)
  hideControls()

  let closed = false
  let releaseWakeLock: (() => void) | null = null
  enterFullscreen()
  void requestWakeLock().then((release) => {
    if (closed) release?.()
    else releaseWakeLock = release
  })

  const cleanup = () => {
    if (closed) return
    closed = true
    releaseWakeLock?.()
    window.clearTimeout(hintTimer)
    window.removeEventListener('keydown', onKey)
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    root.removeEventListener('click', onHiddenStageClick, true)
    prev?.removeEventListener('click', onPrevClick)
    next?.removeEventListener('click', onNextClick)
    mark?.removeEventListener('click', onMarkClick)
    exit?.removeEventListener('click', onExitClick)
    fullscreen?.removeEventListener('click', onFullscreenClick)
    hide?.removeEventListener('click', onHideClick)
    if (document.fullscreenElement === root) void document.exitFullscreen().catch(() => undefined)
    root.hidden = true
    root.classList.remove('is-awaiting-fullscreen')
    hud?.classList.remove('is-hidden')
    if (hint) hint.hidden = true
    document.body.style.overflow = previousBodyOverflow
    if (chrome) chrome.hidden = chromeWasHidden
    if (activeStages.get(root) === cleanup) activeStages.delete(root)
  }

  activeStages.set(root, cleanup)
  return cleanup
}

export async function requestWakeLock(): Promise<(() => void) | null> {
  try {
    // @ts-expect-error Wake Lock is not in all TS libs
    const sentinel = await navigator.wakeLock?.request('screen')
    if (!sentinel) return null
    return () => {
      void sentinel.release()
    }
  } catch {
    return null
  }
}
